import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { GitCompare, Crown, ArrowDownCircle, ArrowUpCircle, History, Swords, Sparkles } from 'lucide-react';
import GlossaryTerm from './GlossaryTerm';

const fmtPct = (v) => (v === null || v === undefined) ? '—' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;
const fmtNum = (v, d = 2) => (v === null || v === undefined) ? '—' : v.toFixed(d);

// Definizione delle righe della tabella confronto: chiave summary + label + formatter + direzione
const METRIC_ROWS = [
  { key: 'sharpe', label: 'Sharpe', term: 'sharpe', fmt: fmtNum, dir: 'higher' },
  { key: 'cagr', label: 'CAGR', term: 'cagr', fmt: fmtPct, dir: 'higher' },
  { key: 'max_dd', label: 'Max DD', term: 'max_dd', fmt: fmtPct, dir: 'higher' }, // -7% > -25%
  { key: 'calmar', label: 'Calmar', term: 'calmar', fmt: fmtNum, dir: 'higher' },
  { key: 'win_rate', label: 'Win rate', term: 'win_rate', fmt: fmtPct, dir: 'higher' },
  { key: 'n_trades', label: 'Trades', term: 'n_trades', fmt: (v) => Math.round(v), dir: 'between', min: 5, max: 200 },
];

const winnerKey = (rows, metric) => {
  // Restituisce l'index della strategia "vincente" per quella metrica.
  // dir 'higher' = max, 'lower' = min, 'between' = quanti rispettano [min,max]
  const vals = rows.map((r) => r.summary?.[metric.key]);
  let bestIdx = -1, bestVal = null;
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i];
    if (v === null || v === undefined || Number.isNaN(v)) continue;
    if (bestIdx < 0) { bestIdx = i; bestVal = v; continue; }
    if (metric.dir === 'higher' && v > bestVal) { bestVal = v; bestIdx = i; }
    if (metric.dir === 'lower' && v < bestVal) { bestVal = v; bestIdx = i; }
    if (metric.dir === 'between') {
      const okV = v >= metric.min && v <= metric.max;
      const okBest = bestVal >= metric.min && bestVal <= metric.max;
      if (okV && !okBest) { bestVal = v; bestIdx = i; }
      else if (okV && okBest && Math.abs(v - (metric.min + metric.max) / 2) < Math.abs(bestVal - (metric.min + metric.max) / 2)) {
        bestVal = v; bestIdx = i;
      }
    }
  }
  return bestIdx;
};

const RoleBadge = ({ role }) => {
  if (role === 'target') return <Badge className="text-[10px] bg-primary/15 text-primary"><Crown className="w-3 h-3 mr-1" />target</Badge>;
  if (role === 'previous_version') return <Badge variant="outline" className="text-[10px]"><History className="w-3 h-3 mr-1" />precedente</Badge>;
  return <Badge variant="outline" className="text-[10px]"><Swords className="w-3 h-3 mr-1" />competitor</Badge>;
};

/**
 * Tabella affiancata multi-strategia per confronto a colpo d'occhio.
 *
 * Props:
 *  - comparison: { target, previous_versions, competitors } (da /strategies/{name}/comparison)
 *  - onProposeEvolution: () => void (apre PromptBuilder con template propose-evolution)
 *  - onSelectStrategy: (name) => void (cambia la strategia attiva)
 */
const StrategyComparison = ({ comparison, onProposeEvolution, onSelectStrategy }) => {
  // Flatten in una lista ordinata: target primo, poi previous_versions, poi competitors
  const rows = useMemo(() => {
    if (!comparison) return [];
    const out = [];
    if (comparison.target) {
      const t = comparison.target;
      out.push({
        name: t.name,
        role: 'target',
        spec: t.spec,
        summary: t.summary?.mean,
        status: t.status,
      });
    }
    (comparison.previous_versions || []).forEach((p) => out.push({
      name: p.name,
      role: 'previous_version',
      spec: p.spec,
      summary: p.summary?.mean,
      status: p.status,
    }));
    (comparison.competitors || []).forEach((c) => out.push({
      name: c.name,
      role: 'competitor',
      spec: c.spec,
      summary: c.summary?.mean,
      status: c.status,
    }));
    return out;
  }, [comparison]);

  const hasComparables = rows.length > 1;

  if (!comparison) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-6 text-sm text-muted-foreground text-center">
          Seleziona una strategia per vedere il confronto.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-primary" />
            Confronto strategie
            <span className="text-xs text-muted-foreground font-normal">
              {rows.length} {rows.length === 1 ? 'strategia' : 'strategie'}
            </span>
          </CardTitle>
          {onProposeEvolution && (
            <Button size="sm" variant="outline" onClick={onProposeEvolution}>
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              Proponi evoluzione
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {!hasComparables ? (
          <div className="text-xs text-muted-foreground py-4">
            Nessun <code>previous_version</code> o <code>compete_with</code> dichiarato in
            <code className="mx-1">lib/strategies/{rows[0]?.name}.json</code>.
            Aggiungili al JSON spec per abilitare il confronto automatico.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="text-left px-2 py-2 min-w-[120px]">Metrica</th>
                {rows.map((r) => {
                  const isCompound = r.spec?.sizing?.method?.endsWith('_compound');
                  return (
                    <th key={r.name} className="text-right px-2 py-2 min-w-[140px]">
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={() => onSelectStrategy?.(r.name)}
                          className="font-mono text-[11px] hover:text-primary transition-colors text-right"
                          title="Vai a questa strategia"
                        >
                          {r.name}
                        </button>
                        <div className="flex items-center gap-1">
                          <RoleBadge role={r.role} />
                          {isCompound && <Badge variant="outline" className="text-[9px] font-mono">cmp</Badge>}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {METRIC_ROWS.map((m) => {
                const winner = winnerKey(rows, m);
                return (
                  <tr key={m.key} className="border-b last:border-0">
                    <td className="px-2 py-1.5 font-medium">
                      <GlossaryTerm term={m.term}>{m.label}</GlossaryTerm>
                    </td>
                    {rows.map((r, idx) => {
                      const v = r.summary?.[m.key];
                      const hasVal = v !== null && v !== undefined && !Number.isNaN(v);
                      const isWinner = hasVal && idx === winner && rows.length > 1;
                      return (
                        <td key={r.name} className={`px-2 py-1.5 text-right tabular-nums ${
                          isWinner ? 'font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30' : ''
                        }`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {hasVal ? m.fmt(v) : '—'}
                            {isWinner && <ArrowUpCircle className="w-3 h-3 text-emerald-600" />}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Riga descrizione strategia (1-line) */}
              <tr className="bg-muted/20">
                <td className="px-2 py-1.5 text-xs text-muted-foreground">Sizing</td>
                {rows.map((r) => (
                  <td key={r.name} className="px-2 py-1.5 text-right text-[10px] text-muted-foreground">
                    {r.spec?.sizing?.method || '—'}
                  </td>
                ))}
              </tr>
              <tr className="bg-muted/10">
                <td className="px-2 py-1.5 text-xs text-muted-foreground">Status</td>
                {rows.map((r) => (
                  <td key={r.name} className="px-2 py-1.5 text-right">
                    {r.status ? (
                      <Badge variant="outline" className="text-[9px]">{r.status}</Badge>
                    ) : <span className="text-muted-foreground text-[10px]">—</span>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
};

export default StrategyComparison;
